import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Loader2,
  Music2,
  FileText,
  Settings
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_KEYS = ["C", "C#", "Db", "D", "D#", "Eb", "E", "F", "F#", "Gb", "G", "G#", "Ab", "A", "A#", "Bb", "B"];

const StagePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  
  const [setlist, setSetlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNotes, setShowNotes] = useState(true);
  const [fontSize, setFontSize] = useState("large"); // small, medium, large, xlarge

  const fetchSetlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/setlists/${id}`, {
        headers: getAuthHeaders()
      });
      setSetlist(response.data);
    } catch (error) {
      toast.error("Error al cargar el setlist");
      navigate("/setlists");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchSetlist();
  }, [fetchSetlist]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!setlist?.songs?.length) return;
      
      switch(e.key) {
        case "ArrowRight":
        case "ArrowDown":
        case " ":
          e.preventDefault();
          setCurrentIndex(prev => Math.min(prev + 1, setlist.songs.length - 1));
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          setCurrentIndex(prev => Math.max(prev - 1, 0));
          break;
        case "Escape":
          navigate(`/setlists/${id}`);
          break;
        case "n":
          setShowNotes(prev => !prev);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setlist, navigate, id]);

  const updateSongKey = async (newKey) => {
    try {
      await axios.put(`${API_URL}/setlists/${id}/songs/${currentIndex}`, {
        transposed_key: newKey
      }, {
        headers: getAuthHeaders()
      });
      fetchSetlist();
    } catch (error) {
      toast.error("Error al cambiar tonalidad");
    }
  };

  const goToSong = (index) => {
    if (index >= 0 && index < setlist.songs.length) {
      setCurrentIndex(index);
    }
  };

  const fontSizeClasses = {
    small: "text-6xl",
    medium: "text-7xl",
    large: "text-8xl",
    xlarge: "text-9xl"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!setlist || !setlist.songs?.length) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4">
        <Music2 className="w-16 h-16 text-gray-700" />
        <p className="text-gray-500 text-xl">El setlist está vacío</p>
        <Link to={`/setlists/${id}`}>
          <Button variant="outline" className="border-white/20 text-white">
            Volver al setlist
          </Button>
        </Link>
      </div>
    );
  }

  const currentSong = setlist.songs[currentIndex];
  const currentKey = currentSong.transposed_key || currentSong.original_key;

  return (
    <div className="min-h-screen bg-black text-white select-none">
      {/* Top Bar - minimal */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm">
        <div className="flex items-center justify-between px-6 h-14">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(`/setlists/${id}`)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
            <span className="text-gray-500 font-medium">{setlist.name}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500 font-mono">
              {currentIndex + 1} / {setlist.songs.length}
            </span>
            
            {/* Font size control */}
            <Select value={fontSize} onValueChange={setFontSize}>
              <SelectTrigger className="w-24 bg-transparent border-white/20 text-gray-400">
                <Settings className="w-4 h-4" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10">
                <SelectItem value="small" className="text-white">Pequeño</SelectItem>
                <SelectItem value="medium" className="text-white">Mediano</SelectItem>
                <SelectItem value="large" className="text-white">Grande</SelectItem>
                <SelectItem value="xlarge" className="text-white">Muy Grande</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Main Content - Full screen */}
      <div className="min-h-screen flex flex-col items-center justify-center pt-14 pb-32 px-8">
        {/* Song Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 text-center font-['Manrope']">
          {currentSong.title}
        </h1>
        {currentSong.artist && (
          <p className="text-xl text-gray-500 mb-8">{currentSong.artist}</p>
        )}
        
        {/* Key - HUGE */}
        <div className="mb-8">
          <Select value={currentKey} onValueChange={updateSongKey}>
            <SelectTrigger className={`${fontSizeClasses[fontSize]} font-bold bg-transparent border-none text-primary hover:text-primary/80 p-0 h-auto font-['JetBrains_Mono']`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10">
              {ALL_KEYS.map(key => (
                <SelectItem key={key} value={key} className="text-white text-2xl font-mono">
                  {key} {key === currentSong.original_key ? "(original)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* BPM Badge */}
        {currentSong.bpm && (
          <Badge className="bg-secondary/20 text-secondary border-secondary/30 text-xl px-4 py-2 font-mono mb-8">
            {currentSong.bpm} BPM
          </Badge>
        )}
        
        {/* Notes */}
        {showNotes && currentSong.notes && (
          <div className="max-w-2xl w-full bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-2 text-gray-500 mb-3">
              <FileText className="w-4 h-4" />
              <span className="text-sm uppercase tracking-wider">Notas</span>
            </div>
            <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-wrap">
              {currentSong.notes}
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          {/* Song list preview */}
          <div className="flex items-center justify-center gap-2 mb-4 overflow-x-auto pb-2">
            {setlist.songs.map((song, i) => (
              <button
                key={i}
                onClick={() => goToSong(i)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg transition-all ${
                  i === currentIndex
                    ? "bg-primary text-white"
                    : i < currentIndex
                      ? "bg-white/10 text-gray-500"
                      : "bg-white/5 text-gray-400 hover:bg-white/10"
                }`}
              >
                <span className="font-mono text-sm">{i + 1}</span>
              </button>
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <Button
              onClick={() => goToSong(currentIndex - 1)}
              disabled={currentIndex === 0}
              variant="outline"
              size="lg"
              className="border-white/20 text-white hover:bg-white/10 gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Anterior
            </Button>
            
            <div className="text-center">
              {currentIndex < setlist.songs.length - 1 && (
                <p className="text-sm text-gray-600">
                  Siguiente: <span className="text-gray-400">{setlist.songs[currentIndex + 1]?.title}</span>
                </p>
              )}
            </div>
            
            <Button
              onClick={() => goToSong(currentIndex + 1)}
              disabled={currentIndex === setlist.songs.length - 1}
              size="lg"
              className="bg-primary hover:bg-primary/90 gap-2"
            >
              Siguiente
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="fixed bottom-4 left-4 text-xs text-gray-700">
        <span>← → navegar • N notas • ESC salir</span>
      </div>
    </div>
  );
};

export default StagePage;
